// Theme system — ported from vanilla THEMES + applyTheme() (public/app/index.html L17938).
// 8 built-in themes: midnight, ocean, ember, daylight, charcoal, slate, dusk, sage.
// applyTheme() overrides CSS custom properties on :root.

export type ThemeKey = 'midnight' | 'ocean' | 'ember' | 'daylight' | 'charcoal' | 'slate' | 'dusk' | 'sage'

interface ThemeSpec {
  bg: string; bgSubtle: string; surface: string; surfaceHover: string; surfaceAlt: string
  border: string; borderStrong: string
  text: string; textMuted: string; textFaint: string
  accent: string; accentLight: string; accentDark: string; accentText: string
  green: string; greenBg: string; greenBorder: string
  amber: string; amberBg: string; amberBorder: string
  red: string;   redBg: string;   redBorder: string
  purple: string; purpleBg: string
  shadow: string; shadowLg: string; glowAccent: string
  light?: boolean  // true = light color-scheme (daylight, sage)
}

export const THEMES: Record<ThemeKey, ThemeSpec> = {
  midnight: {
    bg:'#111110', bgSubtle:'#19191a', surface:'#1e1e1f', surfaceHover:'#262627', surfaceAlt:'#151516',
    border:'rgba(255,255,255,.07)', borderStrong:'rgba(255,255,255,.12)',
    text:'#e8e6e1', textMuted:'#8a8880', textFaint:'#555550',
    accent:'#3ea8a0', accentLight:'rgba(62,168,160,.12)', accentDark:'#2d7a74', accentText:'#5ec4bc',
    green:'#4caf6a', greenBg:'rgba(76,175,106,.1)', greenBorder:'rgba(76,175,106,.25)',
    amber:'#d4a03c', amberBg:'rgba(212,160,60,.1)', amberBorder:'rgba(212,160,60,.25)',
    red:'#cf5454',   redBg:'rgba(207,84,84,.1)',    redBorder:'rgba(207,84,84,.25)',
    purple:'#9b7ed8',purpleBg:'rgba(155,126,216,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.3),0 0 0 1px rgba(255,255,255,.03)',
    shadowLg:'0 4px 24px rgba(0,0,0,.4),0 0 0 1px rgba(255,255,255,.04)',
    glowAccent:'0 0 20px rgba(62,168,160,.15)',
  },
  ocean: {
    bg:'#0f1724', bgSubtle:'#141d30', surface:'#182236', surfaceHover:'#1f2c44', surfaceAlt:'#121a2b',
    border:'rgba(150,180,220,.1)', borderStrong:'rgba(150,180,220,.16)',
    text:'#dde4ef', textMuted:'#7a8ba3', textFaint:'#4a5a72',
    accent:'#4a90d9', accentLight:'rgba(74,144,217,.12)', accentDark:'#3570aa', accentText:'#6bb0f0',
    green:'#4caf6a', greenBg:'rgba(76,175,106,.1)', greenBorder:'rgba(76,175,106,.25)',
    amber:'#d4a03c', amberBg:'rgba(212,160,60,.1)', amberBorder:'rgba(212,160,60,.25)',
    red:'#cf5454',   redBg:'rgba(207,84,84,.1)',    redBorder:'rgba(207,84,84,.25)',
    purple:'#8b9ef0',purpleBg:'rgba(139,158,240,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.4),0 0 0 1px rgba(150,180,220,.04)',
    shadowLg:'0 4px 24px rgba(0,0,0,.5),0 0 0 1px rgba(150,180,220,.05)',
    glowAccent:'0 0 20px rgba(74,144,217,.15)',
  },
  ember: {
    bg:'#161210', bgSubtle:'#1c1714', surface:'#221c18', surfaceHover:'#2c241e', surfaceAlt:'#191412',
    border:'rgba(220,180,140,.08)', borderStrong:'rgba(220,180,140,.14)',
    text:'#ede5dc', textMuted:'#9a8a78', textFaint:'#5e5245',
    accent:'#d4803c', accentLight:'rgba(212,128,60,.12)', accentDark:'#a8642e', accentText:'#e8a060',
    green:'#5aad5e', greenBg:'rgba(90,173,94,.1)', greenBorder:'rgba(90,173,94,.25)',
    amber:'#d4a03c', amberBg:'rgba(212,160,60,.1)', amberBorder:'rgba(212,160,60,.25)',
    red:'#d45454',   redBg:'rgba(212,84,84,.1)',    redBorder:'rgba(212,84,84,.25)',
    purple:'#c09070',purpleBg:'rgba(192,144,112,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.35),0 0 0 1px rgba(220,180,140,.03)',
    shadowLg:'0 4px 24px rgba(0,0,0,.45),0 0 0 1px rgba(220,180,140,.04)',
    glowAccent:'0 0 20px rgba(212,128,60,.15)',
  },
  daylight: {
    bg:'#f5f3f0', bgSubtle:'#edeae6', surface:'#ffffff', surfaceHover:'#faf9f7', surfaceAlt:'#f0eeeb',
    border:'rgba(0,0,0,.08)', borderStrong:'rgba(0,0,0,.14)',
    text:'#2a2926', textMuted:'#6b6860', textFaint:'#9a9790',
    accent:'#2d8a82', accentLight:'rgba(45,138,130,.08)', accentDark:'#237a72', accentText:'#237a72',
    green:'#2e8b57', greenBg:'rgba(46,139,87,.08)', greenBorder:'rgba(46,139,87,.2)',
    amber:'#c4880c', amberBg:'rgba(196,136,12,.08)', amberBorder:'rgba(196,136,12,.2)',
    red:'#c0392b',   redBg:'rgba(192,57,43,.06)',    redBorder:'rgba(192,57,43,.2)',
    purple:'#7c5cbf',purpleBg:'rgba(124,92,191,.08)',
    shadow:'0 1px 3px rgba(0,0,0,.06),0 0 0 1px rgba(0,0,0,.04)',
    shadowLg:'0 4px 24px rgba(0,0,0,.1),0 0 0 1px rgba(0,0,0,.05)',
    glowAccent:'0 0 20px rgba(42,122,111,.1)',
    light: true,
  },
  charcoal: {
    bg:'#0f172a', bgSubtle:'#0a1120', surface:'#1e293b', surfaceHover:'#243044', surfaceAlt:'#162032',
    border:'rgba(51,65,85,.9)', borderStrong:'#475569',
    text:'#f1f5f9', textMuted:'#94a3b8', textFaint:'#64748b',
    accent:'#22c55e', accentLight:'rgba(34,197,94,.1)', accentDark:'#16a34a', accentText:'#22c55e',
    green:'#22c55e', greenBg:'rgba(34,197,94,.08)', greenBorder:'rgba(34,197,94,.2)',
    amber:'#f59e0b', amberBg:'rgba(245,158,11,.1)', amberBorder:'rgba(245,158,11,.2)',
    red:'#ef4444',   redBg:'rgba(239,68,68,.08)',   redBorder:'rgba(239,68,68,.2)',
    purple:'#a78bfa',purpleBg:'rgba(167,139,250,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.3),0 0 0 1px rgba(255,255,255,.03)',
    shadowLg:'0 4px 24px rgba(0,0,0,.4),0 0 0 1px rgba(255,255,255,.04)',
    glowAccent:'0 0 20px rgba(34,197,94,.15)',
  },
  slate: {
    bg:'#1a1d24', bgSubtle:'#1f2229', surface:'#252930', surfaceHover:'#2d323a', surfaceAlt:'#1c2028',
    border:'rgba(140,160,190,.1)', borderStrong:'rgba(140,160,190,.16)',
    text:'#d8dce5', textMuted:'#7b8494', textFaint:'#4e5666',
    accent:'#6b8acd', accentLight:'rgba(107,138,205,.12)', accentDark:'#5270a8', accentText:'#8aa8e8',
    green:'#5cb87a', greenBg:'rgba(92,184,122,.1)', greenBorder:'rgba(92,184,122,.25)',
    amber:'#d4a03c', amberBg:'rgba(212,160,60,.1)', amberBorder:'rgba(212,160,60,.25)',
    red:'#d45c5c',   redBg:'rgba(212,92,92,.1)',    redBorder:'rgba(212,92,92,.25)',
    purple:'#9b88d4',purpleBg:'rgba(155,136,212,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.35),0 0 0 1px rgba(140,160,190,.04)',
    shadowLg:'0 4px 24px rgba(0,0,0,.45),0 0 0 1px rgba(140,160,190,.05)',
    glowAccent:'0 0 20px rgba(107,138,205,.15)',
  },
  dusk: {
    bg:'#18141e', bgSubtle:'#1e1926', surface:'#241e2e', surfaceHover:'#2e2738', surfaceAlt:'#1a1622',
    border:'rgba(180,150,220,.08)', borderStrong:'rgba(180,150,220,.14)',
    text:'#e4ddef', textMuted:'#8e80a4', textFaint:'#5c5070',
    accent:'#a47ad4', accentLight:'rgba(164,122,212,.12)', accentDark:'#8a60b8', accentText:'#c098e8',
    green:'#5cb87a', greenBg:'rgba(92,184,122,.1)', greenBorder:'rgba(92,184,122,.25)',
    amber:'#d4a844', amberBg:'rgba(212,168,68,.1)', amberBorder:'rgba(212,168,68,.25)',
    red:'#d45c6c',   redBg:'rgba(212,92,108,.1)',   redBorder:'rgba(212,92,108,.25)',
    purple:'#b898e0',purpleBg:'rgba(184,152,224,.1)',
    shadow:'0 1px 3px rgba(0,0,0,.4),0 0 0 1px rgba(180,150,220,.03)',
    shadowLg:'0 4px 24px rgba(0,0,0,.5),0 0 0 1px rgba(180,150,220,.04)',
    glowAccent:'0 0 20px rgba(164,122,212,.18)',
  },
  sage: {
    bg:'#f4f6f2', bgSubtle:'#eaede6', surface:'#ffffff', surfaceHover:'#f8faf6', surfaceAlt:'#eef1ea',
    border:'rgba(0,0,0,.07)', borderStrong:'rgba(0,0,0,.13)',
    text:'#2c302a', textMuted:'#68705e', textFaint:'#97a08c',
    accent:'#5a8a5e', accentLight:'rgba(90,138,94,.08)', accentDark:'#4a7a4e', accentText:'#3e7042',
    green:'#3a8a50', greenBg:'rgba(58,138,80,.08)', greenBorder:'rgba(58,138,80,.2)',
    amber:'#b8920e', amberBg:'rgba(184,146,14,.08)', amberBorder:'rgba(184,146,14,.2)',
    red:'#b84040',   redBg:'rgba(184,64,64,.06)',    redBorder:'rgba(184,64,64,.2)',
    purple:'#7a6aaa',purpleBg:'rgba(122,106,170,.08)',
    shadow:'0 1px 3px rgba(0,0,0,.05),0 0 0 1px rgba(0,0,0,.04)',
    shadowLg:'0 4px 24px rgba(0,0,0,.08),0 0 0 1px rgba(0,0,0,.05)',
    glowAccent:'0 0 20px rgba(90,138,94,.1)',
    light: true,
  },
}

/** Apply a theme by setting CSS custom properties on :root — mirrors vanilla applyTheme(). */
export function applyTheme(key: ThemeKey): void {
  const t = THEMES[key]
  if (!t) return
  const s = document.documentElement.style
  s.setProperty('--bg',            t.bg)
  s.setProperty('--bg-subtle',     t.bgSubtle)
  s.setProperty('--surface',       t.surface)
  s.setProperty('--surface-hover', t.surfaceHover)
  s.setProperty('--surface-alt',   t.surfaceAlt)
  s.setProperty('--border',        t.border)
  s.setProperty('--border-strong', t.borderStrong)
  s.setProperty('--text',          t.text)
  s.setProperty('--text-muted',    t.textMuted)
  s.setProperty('--text-faint',    t.textFaint)
  s.setProperty('--accent',        t.accent)
  s.setProperty('--accent-light',  t.accentLight)
  s.setProperty('--accent-dark',   t.accentDark)
  s.setProperty('--accent-text',   t.accentText)
  s.setProperty('--green',         t.green)
  s.setProperty('--green-bg',      t.greenBg)
  s.setProperty('--green-border',  t.greenBorder)
  s.setProperty('--amber',         t.amber)
  s.setProperty('--amber-bg',      t.amberBg)
  s.setProperty('--amber-border',  t.amberBorder)
  s.setProperty('--red',           t.red)
  s.setProperty('--red-bg',        t.redBg)
  s.setProperty('--red-border',    t.redBorder)
  s.setProperty('--purple',        t.purple)
  s.setProperty('--purple-bg',     t.purpleBg)
  s.setProperty('--shadow',        t.shadow)
  s.setProperty('--shadow-lg',     t.shadowLg)
  s.setProperty('--glow-accent',   t.glowAccent)
  document.body.style.colorScheme = t.light ? 'light' : 'dark'
  try { localStorage.setItem('jsp_theme', key) } catch (_) {}
}

/** Read last-used theme from localStorage (used before opts loads). */
export function getStoredTheme(): ThemeKey | null {
  try {
    const v = localStorage.getItem('jsp_theme')
    return v && v in THEMES ? (v as ThemeKey) : null
  } catch (_) {
    return null
  }
}
